/* ------------------------------------------------------------------
   Free gift with purchase  +  bundle discount guard
   ------------------------------------------------------------------
   Two cart-side jobs live here because both need to watch every cart
   change on every page, and this file was already doing that.

   1. FREE GIFT
   ------------
   When the cart contains one of the TRIGGER_SKUS below, the gift
   product picked in Theme settings > Free gift is added to the cart
   automatically. It's removed again as soon as none of the trigger
   SKUs remain.

   - No gift product picked in theme settings -> that half does
     nothing (KROWN.settings.free_gift_variant_id is null).
   - The $0.00 price is handled by a Buy X Get Y automatic discount
     already set up in Shopify admin. This script only makes sure
     the item is in the cart; it never touches price.
   - The auto-added line is tagged with a hidden property so it
     never gets merged with (or accidentally removed alongside) a
     unit of the same product a customer adds on purpose.

   CUSTOMERS CAN DECLINE THE GIFT. The gift line gets its own "Remove"
   link (the normal quantity/remove controls stay hidden for it via
   custom-style.css). Clicking it removes the gift AND sets the hidden
   cart attribute _free_gift_declined = true in the same request, so
   the sync below doesn't add it straight back. The flag is cleared
   once no trigger SKU is left in the cart, so a later qualifying cart
   is offered the gift again.

   BUNDLES SUPPRESS THE GIFT. If any line carries _bundle = true
   (added by the Build Your Own builder), the gift is withheld, and
   removed again if it was already there. Shopify's rule is that
   "Buy X Get Y discounts stack only on the customer-gets products,
   not the customer-buys products" — the diffuser in a bundle is this
   gift's customer-buys product, so the two can't both land. When they
   collide the customer gets billed $8 for an adapter the site
   promised free. A bundle replaces the gift offer instead, and at
   10% off the set the customer is better off anyway.

   2. BUNDLE DISCOUNT GUARD
   ------------------------
   SEASONS-BYO is a discount CODE, applied by the builder on the
   customer's behalf. Shopify keeps an applied code on the SESSION,
   not just on the cart: it survives emptying the cart, and it
   reapplies to whatever the customer puts in next. So a customer who
   used the builder once would keep getting 10% on any later cart of
   3+ items from the Bundle Builder collection, assembled by hand,
   with no builder involved.

   This clears the code whenever it is present and no line in the cart
   carries _bundle = true. POST /cart/update.js with discount: "" is
   what clears it — confirmed against the live cart.

   It only ever clears when SEASONS-BYO is the ONLY code on the cart,
   because that call drops every code and we must not wipe one the
   customer typed themselves.
------------------------------------------------------------------ */

(() => {

  // Edit this list to change which SKUs trigger the free gift.
  // One line per variant SKU — new colourways need adding here too.
  // Note the prefixes are counterintuitive: 002 = Aero SM, 001 = Soni SM.
  const TRIGGER_SKUS = [
    'SSAW-002ALM-2025',  // Aero SM
    'SSAW-002BH',        // Aero SM
    'SSAW-002AWH-2025',  // Aero SM
    'SSAW-002GY',        // Aero SM
    'SSAW-002AMG-2025',  // Aero SM
    'SSAW-001ALM-2025',  // Soni SM
    'SSAW-001ABL-2025',  // Soni SM
    'SSAW-001AGY-2025',  // Soni SM
    'SSAW-001AWH-2025',  // Soni SM
    'SSAW-003DFG',       // Helio Home — Ivory
    'SSAW-003DSG'        // Chroma Home
  ];

  const GIFT_PROPERTY_KEY = '_free_gift';
  const GIFT_PROPERTY_VALUE = 'true';

  // Hidden cart attribute set when the customer removes the gift themselves.
  // Leading underscore keeps it out of the checkout and order summary.
  const DECLINED_ATTRIBUTE = '_free_gift_declined';
  const DECLINED_VALUE = 'true';

  // Set on every line the Build Your Own builder adds.
  const BUNDLE_PROPERTY_KEY = '_bundle';
  const BUNDLE_PROPERTY_VALUE = 'true';

  // Must match the code in Discounts, and the one the builder applies.
  const BUNDLE_DISCOUNT_CODE = 'SEASONS-BYO';

  let isSyncing = false;
  let debounceTimer = null;

  const getGiftVariantId = () => {
    const id = window.KROWN && KROWN.settings && KROWN.settings.free_gift_variant_id;
    return id ? String(id) : '';
  };

  const cartHasTrigger = cart => cart.items.some(item => item.sku && TRIGGER_SKUS.includes(item.sku));

  const cartHasBundle = cart => cart.items.some(item =>
    item.properties &&
    item.properties[BUNDLE_PROPERTY_KEY] === BUNDLE_PROPERTY_VALUE
  );

  const giftDeclined = cart =>
    !!cart.attributes && cart.attributes[DECLINED_ATTRIBUTE] === DECLINED_VALUE;

  const findGiftLine = (cart, variantId) => cart.items.find(item =>
    String(item.variant_id) === variantId &&
    item.properties &&
    item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE
  );

  const refreshCartUI = () => fetch('?section_id=cart-helper')
    .then(response => response.text())
    .then(text => {
      const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
      const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm').innerHTML;
      const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal').innerHTML;

      const cartItems = document.getElementById('AjaxCartForm');
      if (cartItems) {
        cartItems.innerHTML = cartFormInnerHTML;
        if (typeof cartItems.ajaxifyCartItems === 'function') {
          cartItems.ajaxifyCartItems();
        }
      }

      const cartSubtotal = document.getElementById('AjaxCartSubtotal');
      if (cartSubtotal) {
        cartSubtotal.innerHTML = cartSubtotalInnerHTML;
      }

      if (window.preloadImages) window.preloadImages(cartItems);

      const headerCount = document.querySelector('[data-header-cart-count]');
      const cartCount = cartItems && cartItems.querySelector('[data-cart-count]');
      if (headerCount && cartCount) {
        headerCount.textContent = cartCount.textContent;
      }

      addGiftRemoveLinks();
    })
    .catch(() => {});

  const addGift = variantId => fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({
      items: [{
        id: parseInt(variantId, 10),
        quantity: 1,
        properties: { [GIFT_PROPERTY_KEY]: GIFT_PROPERTY_VALUE }
      }]
    })
  });

  const removeGift = lineKey => fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity: 0 })
  });

  /* ---------- customer declines the gift ---------- */

  // One request: drop the gift line and record the choice, so there is no
  // moment where the sync could see "trigger present, no gift, not declined".
  const declineGift = lineKey => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({
      updates: { [lineKey]: 0 },
      attributes: { [DECLINED_ATTRIBUTE]: DECLINED_VALUE }
    })
  });

  const clearDeclined = () => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ attributes: { [DECLINED_ATTRIBUTE]: '' } })
  });

  // The theme's own .actions block is hidden on the gift line, so the gift
  // gets a separate link. Its label copies the theme's "Remove" text so it
  // follows the store language (and Weglot).
  const addGiftRemoveLinks = () => {
    document.querySelectorAll('.cart-item[data-free-gift="true"]').forEach(item => {
      if (item.querySelector('[data-free-gift-remove]')) return;

      const themeRemove = document.querySelector('.cart-item:not([data-free-gift]) .actions .remove');
      const label = themeRemove ? themeRemove.textContent.trim() : 'Remove';

      const wrap = document.createElement('div');
      wrap.className = 'free-gift-actions';
      wrap.style.marginTop = '12px';

      const link = document.createElement('a');
      link.href = '#';
      link.className = 'remove text-size--smallest';
      // Same look as the theme's REMOVE link (its styles are scoped to .actions).
      link.style.cssText = 'display:inline-block;text-transform:uppercase;border-bottom:1px solid;line-height:1;opacity:0.72';
      link.textContent = label;
      link.title = label;
      link.setAttribute('role', 'button');
      link.setAttribute('data-free-gift-remove', '');
      link.setAttribute('data-line-key', item.dataset.id || '');

      wrap.appendChild(link);
      const content = item.querySelector('.content') || item;
      content.appendChild(wrap);
    });
  };

  // Capture phase on document runs before any handler the theme binds to
  // .remove links, so the theme's plain remove (which would let the gift
  // bounce back) never fires for this link.
  document.addEventListener('click', event => {
    const link = event.target.closest && event.target.closest('[data-free-gift-remove]');
    if (!link) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const lineKey = link.getAttribute('data-line-key');
    if (!lineKey || link.dataset.busy) return;
    link.dataset.busy = 'true';

    const item = link.closest('.cart-item');
    if (item) item.style.opacity = '0.5';

    clearTimeout(debounceTimer);
    isSyncing = true;

    declineGift(lineKey)
      .then(refreshCartUI)
      .catch(e => {
        console.log('Free gift remove error:', e);
        if (item) item.style.opacity = '';
        delete link.dataset.busy;
      })
      .finally(() => {
        isSyncing = false;
      });
  }, true);

  /* ---------- the guard ---------- */

  const stuckBundleCode = cart => {
    const codes = cart.discount_codes || [];
    if (!codes.length) return false;

    // Only act when ours is the sole code — clearing drops them all, and a
    // code the customer typed themselves is not ours to throw away.
    if (codes.length > 1) return false;
    if (String(codes[0].code || '').toUpperCase() !== BUNDLE_DISCOUNT_CODE) return false;

    // A cart built through the builder keeps it. Anything else doesn't.
    return !cartHasBundle(cart);
  };

  const clearDiscountCode = () => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ discount: '' })
  });

  /* ---------- the sync ---------- */

  const syncCart = () => {
    if (isSyncing) return;
    isSyncing = true;

    const giftVariantId = getGiftVariantId();

    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Guard first: if the code is stuck on a non-bundle cart, drop it.
        if (stuckBundleCode(cart)) {
          return clearDiscountCode().then(() => {
            // Only repaint when there is something on screen to repaint.
            if (cart.item_count > 0) return refreshCartUI();
          });
        }

        if (!giftVariantId) return;

        const giftLine = findGiftLine(cart, giftVariantId);
        const declined = giftDeclined(cart);

        // No trigger product left: forget an earlier "no thanks" so the next
        // qualifying cart is offered the gift again. Nothing on screen changes.
        if (declined && !cartHasTrigger(cart)) {
          clearDeclined();
        }

        // A bundle in the cart withholds the gift entirely — see the note at
        // the top of this file. The removal branch then strips a gift that was
        // already in the cart before the bundle was added.
        const triggerPresent = cartHasTrigger(cart) && !cartHasBundle(cart);

        if (triggerPresent && !giftLine && !declined) {
          return addGift(giftVariantId).then(refreshCartUI);
        }
        if (!triggerPresent && giftLine) {
          return removeGift(giftLine.key).then(refreshCartUI);
        }
      })
      .catch(e => {
        console.log('Cart sync error:', e);
      })
      .finally(() => {
        isSyncing = false;
        addGiftRemoveLinks();
      });
  };

  const scheduleSync = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(syncCart, 150);
  };

  const watchCartForm = () => {
    const cartFormEl = document.getElementById('AjaxCartForm');
    if (!cartFormEl) return;

    new MutationObserver(() => {
      addGiftRemoveLinks();
      if (!isSyncing) scheduleSync();
    }).observe(cartFormEl, { childList: true });
  };

  document.addEventListener('DOMContentLoaded', () => {
    // The guard runs whether or not a gift product is configured, so it is
    // never switched off by a change to the free-gift settings.
    addGiftRemoveLinks();
    watchCartForm();
    syncCart();
  });

})();
